import {
  select,
  cancelled as cancelledEffect,
  call,
  put,
} from 'redux-saga/effects';

import filterApiFactory from '../../modules/filter/filterApi';

/* Utils */
import {generateDefaultAsyncWatcher, generateDefaultAsyncSaga} from '../core/reduxUtils';
import {getCookies} from '../../common/network/apiUtils';

/* Constants */
import {FACET_QUERY_PARAM_NAMES} from '../../views/filter/facet/facetConstants';
import {IGNORE_GENDER_QUERY_PARAM, IGNORE_COLOR_QUERY_PARAM, IGNORE_TOLKIEN_QUERY_PARAM} from '../../modules/search/searchConstants';

/* Action */
import {
  applyFiltersActionCreators,
  applyTagFiltersActionCreators,
  applySearchFiltersActionCreators,
  getFiltersActionCreators,
  getNextFiltersActionCreators,
  getNextSearchFiltersActionCreators,
  getNextTagFiltersActionCreators,
  getSearchFiltersActionCreators,
  getTagFiltersActionCreators,
  getUpdateFilterSelectionActionCreators,
  getDeliveryInfoActionCreators,
  getBrandFiltersActionCreators,
  getNextBrandFiltersActionCreators,
  applyBrandFiltersActionCreators,
  getCampaignFilterActionCreators,
  getQuickFilterDeliveryInfoActionCreators,
} from '../actions/filterActions';

/* Selectors */
import {
  categoryFacetSelector,
  filterApiQueryParamsSelector,
  filterApiQueryParamsStringSelector,
  ignoreTolkienForSearchFiltersSelector,
  isRemoveAutoAppliedFacetForBrand,
  isRemoveAutoAppliedFacetForGender,
  isRemoveAutoAppliedFacetForColor,
  searchContextForSearchFiltersSelector,
  tagIdsMatchParamSelector,
  searchTermTypoForSearchFiltersSelector,
  brandIdsMatchParamSelector,
  autoAppliedFacetCountSelector,
  ignoreGenderForSearchFiltersSelector,
  ignoreColorForSearchFiltersSelector,
  initialFilteredCategoryLevelSelector,
} from '../selectors/filterSelectors';

function filterSagasFactory({storefrontApiClient}) {
  const filterApi = filterApiFactory(storefrontApiClient);

  const getFiltersSaga = generateDefaultAsyncSaga(
    filterApi.getFilters,
    getFiltersActionCreators,
    payload => [
      payload.params,
      payload.cookies,
    ],
  );

  const getSearchFiltersSaga = generateDefaultAsyncSaga(
    filterApi.getSearchFilters,
    getSearchFiltersActionCreators,
    payload => [
      payload.params,
      payload.cookies,
      payload.headers
    ],
  );

  const getNextFiltersSaga = generateDefaultAsyncSaga(
    filterApi.getNextFilters,
    getNextFiltersActionCreators,
    payload => [
      payload.facetId,
      payload.params,
      payload.cookies,
    ],
  );

  const getNextSearchFiltersSaga = generateDefaultAsyncSaga(
    filterApi.getSearchNextFilters,
    getNextSearchFiltersActionCreators,
    payload => [
      payload.facetId,
      payload.params,
      payload.cookies,
    ],
  );

  const getTagFiltersSaga = generateDefaultAsyncSaga(
    filterApi.getTagFilters,
    getTagFiltersActionCreators,
    payload => [
      payload.params,
      payload.cookies,
    ],
  );

  const getNextTagFiltersSaga = generateDefaultAsyncSaga(
    filterApi.getTagNextFilters,
    getNextTagFiltersActionCreators,
    payload => [
      payload.facetId,
      payload.params,
      payload.cookies,
    ],
  );

  const getBrandFiltersSaga = generateDefaultAsyncSaga(
    filterApi.getBrandFilters,
    getBrandFiltersActionCreators,
    payload => [
      payload.params,
      payload.cookies,
    ],
  );

  const getNextBrandFiltersSaga = generateDefaultAsyncSaga(
    filterApi.getBrandNextFilters,
    getNextBrandFiltersActionCreators,
    payload => [
      payload.facetId,
      payload.params,
      payload.cookies,
    ],
  );

  const getUpdateFilterSelectionSaga = generateDefaultAsyncSaga(
    () => {
      return Promise.resolve()
    },
    getUpdateFilterSelectionActionCreators,
    payload => [
      payload.facetId,
      payload.filter,
      payload.isSingleSelection,
      payload.category,
    ],
  );

  const getDeliveryInfoSaga = generateDefaultAsyncSaga(
    filterApi.getJetDeliveryInfo,
    getDeliveryInfoActionCreators,
    payload => [
      payload.params,
      payload.cookies,
    ],
  );

  const getQuickFilterDeliveryInfoSaga = generateDefaultAsyncSaga(
    data => data,
    getQuickFilterDeliveryInfoActionCreators,
    payload => [
      payload.params,
      payload.cookies,
    ],
  );

  const getCampaignFilterSaga = generateDefaultAsyncSaga(
    filterApi.getCampaignFilter,
    getCampaignFilterActionCreators,
    payload => [
      payload.params,
      payload.cookies,
    ],
  );

  function* applyFiltersSaga(action) {
    const {
      payload,
      payload: {cookies = {}, params = {}},
    } = action;
    let result;

    try {
      const filterApiQueryParams = yield select(filterApiQueryParamsSelector);
      const categoryFacet = yield select(categoryFacetSelector);
      const response = yield call(getFiltersSaga, {
        payload: {
          params: {
            [FACET_QUERY_PARAM_NAMES.SORT]: params?.[FACET_QUERY_PARAM_NAMES.SORT],
            ...filterApiQueryParams,
            [FACET_QUERY_PARAM_NAMES.CATEGORY]: categoryFacet.categoryId,
          },
          cookies
        },
      });

      yield put(applyFiltersActionCreators.fulfill(response, payload));
      result = response;
    } catch (error) {
      yield put(applyFiltersActionCreators.reject(error, payload));
      result = error;
    } finally {
      if (yield cancelledEffect()) {
        yield put(applyFiltersActionCreators.cleanup(payload));
      }
    }

    return result;
  }

  function* applySearchFiltersSaga(action) {
    const {
      payload,
      payload: {cookies = {}},
    } = action;
    let result;

    try {
      const filterApiQueryParams = yield select(filterApiQueryParamsSelector);
      const filterApiQueryParamsString = yield select(filterApiQueryParamsStringSelector);
      const searchContext = yield select(searchContextForSearchFiltersSelector);
      const searchTermTypo = yield select(searchTermTypoForSearchFiltersSelector);

      const isIgnoreTolkien = yield select(ignoreTolkienForSearchFiltersSelector);
      const isRemoveAutoAppliedBrand = yield select(isRemoveAutoAppliedFacetForBrand);
      const isRemoveAutoAppliedGender = yield select(isRemoveAutoAppliedFacetForGender);
      const isRemoveAutoAppliedColor = yield select(isRemoveAutoAppliedFacetForColor);
      const isIgnoreGenderFilter = yield select(ignoreGenderForSearchFiltersSelector);
      const isIgnoreColorFilter = yield select(ignoreColorForSearchFiltersSelector);

      let isIgnoreGender = isRemoveAutoAppliedGender || isIgnoreGenderFilter;
      let isIgnoreColor = isRemoveAutoAppliedColor || isIgnoreColorFilter;
      let isIgnoreBrand = isIgnoreTolkien || isRemoveAutoAppliedBrand;

      const filteredCategoryLevel = yield select(initialFilteredCategoryLevelSelector)
      const autoAppliedCount = yield select(autoAppliedFacetCountSelector);

      let query = searchContext.originalSearchTerm;

      if (autoAppliedCount === 1 && filteredCategoryLevel === 0) {
        if (isIgnoreBrand || isIgnoreGender || isIgnoreColor) {
          query = searchContext.trimmedSearchTerm;
        }
      }

      const response = yield call(getSearchFiltersSaga, {
        payload: {
          params: {
            ...filterApiQueryParams,
            [FACET_QUERY_PARAM_NAMES.QUERY]: query,
            [FACET_QUERY_PARAM_NAMES.CATEGORY]: payload.params[FACET_QUERY_PARAM_NAMES.CATEGORY],
            [FACET_QUERY_PARAM_NAMES.IGNORE_TOLKIEN]: isIgnoreBrand ? IGNORE_TOLKIEN_QUERY_PARAM.VALUE : null,
            [FACET_QUERY_PARAM_NAMES.IGNORE_TYPO]: payload.params[FACET_QUERY_PARAM_NAMES.IGNORE_TYPO] || null,
            [FACET_QUERY_PARAM_NAMES.IGNORE_CATEGORY]: payload.params[FACET_QUERY_PARAM_NAMES.IGNORE_CATEGORY] || null,
            [FACET_QUERY_PARAM_NAMES.IGNORE_GENDER]: isIgnoreGender ? IGNORE_GENDER_QUERY_PARAM.VALUE : null,
            [FACET_QUERY_PARAM_NAMES.IGNORE_COLOR]: isIgnoreColor ? IGNORE_COLOR_QUERY_PARAM.VALUE : null,
          },
          cookies,
        },
      });

      yield put(applySearchFiltersActionCreators.fulfill(response, payload));
      result = response;
    } catch (error) {
      yield put(applySearchFiltersActionCreators.reject(error, payload));
      result = error;
    } finally {
      if (yield cancelledEffect()) {
        yield put(applySearchFiltersActionCreators.cleanup(payload));
      }
    }

    return result;
  }

  function* applyTagFiltersSaga(action) {
    const {
      payload,
      payload: {cookies = {}},
    } = action;
    let result;

    try {
      const filterApiQueryParams = yield select(filterApiQueryParamsSelector);
      const tagId = yield select(tagIdsMatchParamSelector);
      const response = yield call(getTagFiltersSaga, {
        payload: {
          params: {
            ...filterApiQueryParams,
            [FACET_QUERY_PARAM_NAMES.TAG_ID]: tagId,
          },
          cookies
        },
      });

      yield put(applyTagFiltersActionCreators.fulfill(response, payload));
      result = response;
    } catch (error) {
      yield put(applyTagFiltersActionCreators.reject(error, payload));
      result = error;
    } finally {
      if (yield cancelledEffect()) {
        yield put(applyTagFiltersActionCreators.cleanup(payload));
      }
    }

    return result;
  }

  function* applyBrandFiltersSaga(action) {
    const {
      payload,
    } = action;
    let result;

    try {
      const filterApiQueryParams = yield select(filterApiQueryParamsSelector);
      const brandId = yield select(brandIdsMatchParamSelector);
      const response = yield call(getBrandFiltersSaga, {
        payload: {
          params: {
            ...filterApiQueryParams,
            [FACET_QUERY_PARAM_NAMES.BRAND_PAGE]: brandId,
          },
        },
      });

      yield put(applyBrandFiltersActionCreators.fulfill(response, payload));
      result = response;
    } catch (error) {
      yield put(applyBrandFiltersActionCreators.reject(error, payload));
      result = error;
    } finally {
      if (yield cancelledEffect()) {
        yield put(applyBrandFiltersActionCreators.cleanup(payload));
      }
    }

    return result;
  }

  const watchGetFilters = generateDefaultAsyncWatcher(
    'GET_FILTERS',
    getFiltersSaga,
  );

  const watchGetSearchFilters = generateDefaultAsyncWatcher(
    'GET_SEARCH_FILTERS',
    getSearchFiltersSaga,
  );

  const watchGetTagFilters = generateDefaultAsyncWatcher(
    'GET_TAG_FILTERS',
    getTagFiltersSaga,
  );

  const watchGetNextTagFilters = generateDefaultAsyncWatcher(
    'GET_NEXT_TAG_FILTERS',
    getNextTagFiltersSaga,
  );

  const watchGetBrandFilters = generateDefaultAsyncWatcher(
    'GET_BRAND_FILTERS',
    getBrandFiltersSaga,
  );

  const watchGetNextBrandFilters = generateDefaultAsyncWatcher(
    'GET_NEXT_BRAND_FILTERS',
    getNextBrandFiltersSaga,
  );

  const watchGetNextFilters = generateDefaultAsyncWatcher(
    'GET_NEXT_FILTERS',
    getNextFiltersSaga,
  );

  const watchGetNextSearchFilters = generateDefaultAsyncWatcher(
    'GET_NEXT_SEARCH_FILTERS',
    getNextSearchFiltersSaga,
  );

  const watchApplyFiltersSaga = generateDefaultAsyncWatcher(
    'APPLY_FILTERS',
    applyFiltersSaga,
  );

  const watchSearchApplyFiltersSaga = generateDefaultAsyncWatcher(
    'APPLY_SEARCH_FILTERS',
    applySearchFiltersSaga,
  );

  const watchTagApplyFiltersSaga = generateDefaultAsyncWatcher(
    'APPLY_TAG_FILTERS',
    applyTagFiltersSaga,
  );

  const watchBrandApplyFiltersSaga = generateDefaultAsyncWatcher(
    'APPLY_BRAND_FILTERS',
    applyBrandFiltersSaga,
  );

  const watchUpdateFilterSelectionFiltersSaga = generateDefaultAsyncWatcher(
    'GET_UPDATE_FILTER_SELECTION',
    getUpdateFilterSelectionSaga,
  );

  const watchDeliveryInfoSaga = generateDefaultAsyncWatcher(
    'GET_DELIVERY_INFO',
    getDeliveryInfoSaga,
  );

  const watchQuickFilterDeliveryInfoSaga = generateDefaultAsyncWatcher(
    'GET_QUICK_FILTER_DELIVERY_INFO',
    getQuickFilterDeliveryInfoSaga,
  );

  const watchGetCampaignFilterSaga = generateDefaultAsyncWatcher(
    'GET_CAMPAIGN_FILTER',
    getCampaignFilterSaga,
  );

  const filterSagas = [
    watchApplyFiltersSaga(),
    watchGetFilters(),
    watchGetNextFilters(),
    watchGetNextSearchFilters(),
    watchGetSearchFilters(),
    watchSearchApplyFiltersSaga(),
    watchGetTagFilters(),
    watchGetNextTagFilters(),
    watchTagApplyFiltersSaga(),
    watchUpdateFilterSelectionFiltersSaga(),
    watchDeliveryInfoSaga(),
    watchQuickFilterDeliveryInfoSaga(),
    watchGetBrandFilters(),
    watchGetNextBrandFilters(),
    watchBrandApplyFiltersSaga(),
    watchGetCampaignFilterSaga(),
  ];

  return filterSagas;
}

export default filterSagasFactory;
